import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Component, DestroyRef, ElementRef, EventEmitter, HostListener, inject, Inject, Output, PLATFORM_ID, ViewChild, OnInit, Input, ChangeDetectorRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SafeResourceUrl, DomSanitizer } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from '../../../core/services/authentication.service';
import { BootstrapModalService } from '../../../core/services/bootstrap-modal.service';
import { LoaderService } from '../../../core/services/loader.service';
import { ProfileService } from '../../../features/profile/profile.service';
import { PostModel, PostMediaModel, PostLangModel } from '../../../models/post.model';
import { FlashMessageService } from '../flash-message/flash-message.service';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { SsrSafeRichTextEditorComponent } from '../../ssr-safe-rich-text-editor.component';
import { AddUpdatePostService } from './add-update-post.service';
import { PostRefreshService } from '../../services/post-refresh.service';

@Component({
  selector: 'app-add-update-post',
  imports: [CommonModule, FormsModule, TranslateModule, SsrSafeRichTextEditorComponent],
  templateUrl: './add-update-post.component.html',
  styleUrl: './add-update-post.component.scss'
})
export class AddUpdatePostComponent implements OnInit {

  @ViewChild('editor') editor!: ElementRef<HTMLDivElement>;
  @ViewChild('imageInput') imageInput!: ElementRef<HTMLInputElement>;
  @ViewChild('pdfInput') pdfInput!: ElementRef<HTMLInputElement>;
  @ViewChild('emojiWrapper') emojiWrapper!: ElementRef;
  @ViewChild('pickerContainer') pickerContainer!: ElementRef;


  @Input() isEditMode?: boolean = false;
  @Input() postUid?: string | null;
  @Output() closeModal = new EventEmitter<void>();
  @Output() postPublished = new EventEmitter<PostModel>();
  @Output() exit = new EventEmitter<string>();

  private readonly destroyRef = inject(DestroyRef);
  public isBrowser: boolean;
  CompanyPost: PostModel = new PostModel();
  selectedPostLang: string = '';
  langErrors: { [key: string]: { desc: boolean } | undefined } = {};
  lastEditedLang = '';
  media = { images: [] as PostMediaModel[], pdfs: [] as PostMediaModel[] };
  tempUid: string = '';
  emojiData: any = null;
  pickerInitialized: boolean = false;
  showPicker: boolean = false;
  message: string = '';
  selectedLang = '';
  imageUrl: string = '';
  pdf!: File | null;
  pdfUrl: SafeResourceUrl | null = null;
  showPdfModal: boolean = false;
  deleteContext: { type: string, data: any } | null = null;

  constructor(@Inject(PLATFORM_ID) private readonly platformId: object,
    @Inject(DOCUMENT) private readonly document: Document,
    public loaderService: LoaderService,
    private readonly addUpdatePostService: AddUpdatePostService,
    public translate: TranslateService,
    public authenticationService: AuthenticationService,
    private readonly profileService: ProfileService,
    public readonly router: Router,
    private readonly bootstrapModalService: BootstrapModalService,
    private readonly sanitizer: DomSanitizer,
    private readonly flashMessage: FlashMessageService,
    private readonly cdr: ChangeDetectorRef,
    private readonly route: ActivatedRoute,
    private readonly postRefreshService: PostRefreshService
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.selectedLang = this.translate.getCurrentLang();
  }

  ngOnInit(): void {
    this.selectedLang = this.translate.getCurrentLang();
    if (this.isEditMode) {
      this.initMethod();
    }
    if (!this.isEditMode && this.isBrowser) {
      this.CompanyPost = new PostModel();
      this.getCompanyDetails();
      this.loaderService.display(false);
      this.tempUid = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
    }
  }

  initMethod() {
    this.loaderService.display(true);
    this.addUpdatePostService.getPostDetailsByPostUid(this.postUid!).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          this.CompanyPost = data;
          if (this.CompanyPost?.PostPdf?.Url) {
            this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.CompanyPost.PostPdf.Url);
          }
          this.selectedPostLang = this.selectedLang;
          for (let lang of this.CompanyPost.PostDescr) {
            this.langErrors[lang.Language] = { desc: false };
          }
          this.loaderService.display(false);
        },
        error: () => {
          this.flashMessage.showErrorAlert(this.translate.instant('error-message'));
          this.loaderService.display(false);
        }
      });
  }

  async togglePicker(event: Event) {
    if (!this.isBrowser) return;
    event.stopPropagation();
    this.showPicker = !this.showPicker;
    if (this.showPicker) {
      await this.initPicker();
    }
  }

  async loadEmojiData() {
    if (this.emojiData) return this.emojiData;
    const module = await import('@emoji-mart/data');
    this.emojiData = module.default;
    return this.emojiData;
  }

  async initPicker() {
    if (!this.isBrowser || this.pickerInitialized) return;
    const data = await this.loadEmojiData();
    const { Picker } = await import('emoji-mart');
    this.pickerContainer.nativeElement.innerHTML = '';
    const picker = new Picker({ data, onEmojiSelect: (emoji: any) => { this.onEmojiSelect(emoji); } });
    this.pickerContainer.nativeElement.appendChild(picker)
  }

  onEmojiSelect(emoji: any) {
    const emojiChar = emoji.native;
    const current = this.selectedLangData.Description || '';
    this.selectedLangData.Description = current + emojiChar;
  }

  @HostListener('document:click', ['$event'])
  handleClick(event: Event) {
    if (!this.isBrowser) return;
    if (this.showPicker && this.emojiWrapper && !this.emojiWrapper.nativeElement.contains(event.target)) {
      this.showPicker = false;
    }
  }

  getCompanyDetails() {
    this.loaderService.display(true);
    this.profileService.getCompanyDetails().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        this.CompanyPost = data;
        this.selectedPostLang = this.selectedLang;
        for (let lang of this.CompanyPost.PostDescr) {
          this.langErrors[lang.Language] = { desc: false };
        }
        this.loaderService.display(false);
      },
      error: () => {
        this.flashMessage.showErrorAlert(this.translate.instant("error-message"));
        this.loaderService.display(false);
      }
    });
  }

  onChangeLanguage(langCode: string) {
    this.selectedPostLang = langCode;
    this.validateAllLanguages();
  }

  get selectedLangData(): PostLangModel {
    if (!Array.isArray(this.CompanyPost.PostDescr)) {
      this.CompanyPost.PostDescr = [];
    }

    let found = this.CompanyPost.PostDescr.find(x => x.Language === this.selectedPostLang);
    if (!found) {
      found = new PostLangModel({
        PostUid: this.CompanyPost.PostUid,
        Language: this.selectedPostLang
      });
      this.CompanyPost.PostDescr.push(found);
    }
    return found;
  }

  validateAllLanguages() {
    this.langErrors = {};
    for (let lang of this.CompanyPost.PostDescr) {
      const key = lang.Language;
      const desc = this.stripHtml(lang.Description);
      this.langErrors[key] = { desc: false };

      if (key === this.selectedLang) {
        this.langErrors[key].desc = desc.length === 0;
      }
    }
  }

  canPublish(): boolean {
    const selectedLangData = this.CompanyPost.PostDescr.find(
      lang => lang.Language === this.selectedLang
    );
    const desc = this.stripHtml(selectedLangData?.Description);
    return desc.length > 0;
  }

  close() {
    this.closeModal.emit();
  }

  onContentChange(html: string) {
    const current = this.selectedLangData;
    current.Description = html;
    this.validateAllLanguages();
  }

  onEditorExit(html: string) {
    const text = this.stripHtml(html);
    const current = this.selectedLangData;
    if (!text) {
      current.Description = '';
    }
    this.validateAllLanguages();
  }

  triggerImageUpload() {
    this.imageInput.nativeElement.click();
  }

  triggerPdfUpload() {
    this.pdfInput.nativeElement.click();
  }

  onFileUpload(event: Event, mediaType: string): void {
    if (!this.isBrowser) return;
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    if (!this.validateFile(file, mediaType)) {
      input.value = '';
      return;
    }
    if (mediaType === 'pdf') {
      this.pdf = file;
    }
    this.uploadPostMedia(file, mediaType);
    input.value = '';
  }

  validateFile(file: File, mediaType: string): boolean {
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      this.flashMessage.showWarningAlert(this.translate.instant('file_must_be_less_than_5mb'));
      return false;
    }

    if (mediaType === "image" && !file.type.startsWith('image/')) {
      this.flashMessage.showWarningAlert(this.translate.instant('invalid_image_file'));
      return false;
    }

    if (mediaType === "pdf" && file.type !== 'application/pdf') {
      this.flashMessage.showWarningAlert(this.translate.instant('only_pdf_files_allowed'));
      return false;
    }

    return true;
  }

  uploadPostMedia(file: File, mediaType: string): void {
    const formData = new FormData();
    if (this.isEditMode) {
      formData.append("PostUid", this.CompanyPost.PostUid.toString());
    } else {
      formData.append("TempUid", this.tempUid);
    }
    formData.append("file", file);
    this.loaderService.display(true);
    this.addUpdatePostService.uploadPostMedia(formData).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: { Url: string; PseudoName: string }) => {
        const mediaItem = new PostMediaModel({
          Url: data.Url,
          PseudoName: data.PseudoName,
          OriginalName: file.name,
          TempUid: this.tempUid
        });

        this.addMedia(mediaItem, mediaType);
        this.loaderService.display(false);
      },
      error: () => {
        this.pdf = null
        this.loaderService.display(false);
        this.flashMessage.showErrorAlert(this.translate.instant('error-message'));
      }
    });
  }

  addMedia(media: PostMediaModel, mediaType: string): void {
    switch (mediaType) {
      case 'image':
        this.CompanyPost.PostImage = media;
        break;

      case 'pdf':
        this.CompanyPost.PostPdf = media;
        break;
    }
  }

  onPublishPost() {
    this.loaderService.display(true);
    if (this.isEditMode) {
      this.addUpdatePostService.updatePost(this.CompanyPost).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.postPublished.emit(this.CompanyPost);
          this.loaderService.display(false);
        },
        error: () => {
          this.loaderService.display(false);
          this.flashMessage.showErrorAlert(this.translate.instant('error-message'));
        }
      });
    }
    else {
      this.CompanyPost.TempUid = this.tempUid;
      this.addUpdatePostService.createPost(this.CompanyPost).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.bootstrapModalService.close('publishPostModal');
          this.cdr.detectChanges();
          this.bootstrapModalService.open('PublishSuccessModal');
          this.postRefreshService.refreshPosts();
          this.postPublished.emit(this.CompanyPost);
          this.loaderService.display(false);
        },
        error: () => {
          this.loaderService.display(false);
          this.flashMessage.showErrorAlert(this.translate.instant('error-message'));
        }
      });
    }
  }

  deleteMedia(media: PostMediaModel, mediaType: string) {
    this.deleteContext = { type: mediaType, data: media };
    this.bootstrapModalService.open('postConfirmModal');
  }

  confirmDelete() {
    if (!this.deleteContext) return;
    const { type, data } = this.deleteContext;
    switch (type) {
      case 'image':
        this.deleteImage(data);
        break;
      case 'pdf':
        this.deletePdf(data);
    }
    this.deleteContext = null;
  }


  deleteImage(img: PostMediaModel) {
    const formData = new FormData();
    formData.append("PseudoName", img.PseudoName);
    formData.append("IsImage", 'true');
    if (this.isEditMode) {
      formData.append("postUid", this.CompanyPost.PostUid);
    } else {
      formData.append("TempUid", this.tempUid);
    }
    this.loaderService.display(true);
    this.addUpdatePostService.deletePostMedia(formData).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.CompanyPost.PostImage = new PostMediaModel();
        this.loaderService.display(false);
        this.flashMessage.showSuccessAlert(this.translate.instant('image_deleted_succcessfully'));
      },
      error: () => {
        this.loaderService.display(false);
        this.flashMessage.showErrorAlert(this.translate.instant("error-message"));
      }
    });
  }

  deletePdf(pdf: PostMediaModel) {
    const formData = new FormData();
    formData.append("PseudoName", pdf.PseudoName);
    formData.append("IsImage", 'false');
    if (this.isEditMode) {
      formData.append("postUid", this.CompanyPost.PostUid);
    } else {
      formData.append("TempUid", this.tempUid);
    }
    this.loaderService.display(true);
    this.addUpdatePostService.deletePostMedia(formData).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.CompanyPost.PostPdf = new PostMediaModel();
        this.loaderService.display(false);
        this.flashMessage.showSuccessAlert(this.translate.instant('pdf_deleted_successfully'));
      },
      error: () => {
        this.loaderService.display(false);
        this.flashMessage.showErrorAlert(this.translate.instant("error-message"));
      }
    });
  }

  previewFile() {
    if (!this.CompanyPost?.PostPdf?.Url || !this.isBrowser) {
      return;
    }
    this.loaderService.display(true);
    if (this.isEditMode) {
      let pdfPath = this.CompanyPost.PostUid + '/' + this.CompanyPost.PostPdf.PseudoName;
      this.addUpdatePostService.getPostPdf(pdfPath).pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (blob: Blob) => {
            const blobUrl = URL.createObjectURL(blob);
            this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);
            this.showPdfModal = true;
            this.loaderService.display(false);
          },
          error: () => {
            this.loaderService.display(false);
            this.flashMessage.showErrorAlert(this.translate.instant('error-message'));
          }
        });
    } else {
      if (!this.pdf) {
        this.loaderService.display(false);
        return;
      }
      const blobUrl = URL.createObjectURL(this.pdf);
      this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);
      this.showPdfModal = true;
      this.loaderService.display(false);
    }
  }

  closePdfModal() {
    this.showPdfModal = false;
    this.pdfUrl = null;
  }

  onCancelConfirmModal() {
    this.deleteContext = null;
    this.bootstrapModalService.close('postConfirmModal');
  }

  goToCompanyPage() {
    this.bootstrapModalService.close(this.isEditMode ? 'EditSuccessModal' : 'PublishSuccessModal');
    this.cdr.detectChanges();
    void this.router.navigate(['/bim/company', this.CompanyPost.CompanyShortName, 'posts']);
  }

  private stripHtml(html: string | null | undefined): string {
    if (!html) {
      return '';
    }
    const element = this.document.createElement('div');
    element.innerHTML = html;
    return element.textContent?.trim() || '';
  }

}
